import React from 'react';
import { Box, Flex, SimpleGrid, Skeleton, Text } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import {
  ArrowsClockwise,
  CheckCircle,
  Question,
  Receipt,
  ShieldWarning,
  WarningCircle,
} from 'phosphor-react';

import { GlassCard } from '../../../../../../components/glass';
import type { IExtratoResumo, IExtratoStatus } from '../../../../../../hooks/useContaCilExtratos';
import { fmtNumero } from '../format';

/** '' = todos. Um status, ou VÁRIOS separados por vírgula — o backend aceita
 *  lista (o card "Aguardando" manda os três estados de espera de uma vez). */
export type FiltroStatus = '' | IExtratoStatus | `${IExtratoStatus},${string}`;

interface KpiDef {
  value: FiltroStatus;
  label: string;
  countKey: keyof IExtratoResumo;
  /** Quando presente, o número do card é a SOMA destas chaves. */
  somaKeys?: (keyof IExtratoResumo)[];
  /** Card que agrega mais de um status oferece um segundo nível de filtro,
   *  senão a lista fica grande demais pra encontrar alguma coisa. */
  subFiltros?: { value: string; label: string }[];
  /** Destaca o card quando a contagem é > 0 (documento parado pedindo ação).
   *  Flag própria de propósito: antes isto era inferido comparando `value`
   *  contra literais, e ao agregar status no `value` os cards "A identificar"
   *  e "Com erro" perderam o destaque em silêncio — o TypeScript não pega,
   *  porque comparar duas strings válidas é legítimo. */
  requerAtencao?: boolean;
  icon: React.ReactNode;
  accent: string;
}

const KPIS: KpiDef[] = [
  {
    value: '',
    label: 'Total',
    countKey: 'total',
    icon: <Receipt size={20} weight="duotone" />,
    accent: '#6366F1',
  },
  {
    value: 'concluido',
    label: 'Concluídos',
    countKey: 'concluido',
    icon: <CheckCircle size={20} weight="duotone" />,
    accent: '#10B981',
  },
  {
    value: 'processando',
    label: 'Processando',
    countKey: 'processando',
    icon: <ArrowsClockwise size={20} weight="duotone" />,
    accent: '#1B88F5',
  },
  {
    // Agrega `aguardando_titular`: os dois são "não sei de quem é este extrato"
    // — um sem nada pra identificar, outro com banco mas sem CNPJ. Antes o
    // `aguardando_titular` não tinha card e ficava fora dos KPIs.
    value: 'a_identificar,aguardando_titular',
    requerAtencao: true,
    label: 'A identificar',
    countKey: 'a_identificar',
    somaKeys: ['a_identificar', 'aguardando_titular'],
    subFiltros: [
      { value: 'a_identificar', label: 'A identificar' },
      { value: 'aguardando_titular', label: 'Aguardando titular' },
    ],
    icon: <Question size={20} weight="duotone" />,
    accent: '#F59E0B',
  },
  {
    value: 'aguardando_validacao',
    requerAtencao: true,
    label: 'Aguardando',
    countKey: 'aguardando_validacao',
    icon: <ShieldWarning size={20} weight="duotone" />,
    accent: '#EAB308',
  },
  {
    // Agrega `quarentena`: nos dois o documento não foi processado. O ETL manda
    // quarentena quando o parse não conclui, e hoje boa parte deles é FALHA com
    // rótulo trocado (bug do `motivo_quarentena`, correção pendente no ETL).
    // Sem card, ficavam invisíveis — e com `empresa_id` nulo, nem o filtro por
    // empresa alcançava.
    value: 'erro,quarentena',
    requerAtencao: true,
    label: 'Com erro',
    countKey: 'erro',
    somaKeys: ['erro', 'quarentena'],
    subFiltros: [
      { value: 'erro', label: 'Com erro' },
      { value: 'quarentena', label: 'Quarentena' },
    ],
    icon: <WarningCircle size={20} weight="duotone" />,
    accent: '#EF4444',
  },
];

/** Cards que agregam mais de um status e por isso oferecem segundo nível de
 *  filtro. Exportado pra tela montar o controle sem duplicar a definição. */
export const KPIS_COM_SUBFILTRO = KPIS.filter(k => k.subFiltros).map(k => ({
  value: k.value,
  subFiltros: k.subFiltros as { value: string; label: string }[],
}));

interface Props {
  resumo?: IExtratoResumo;
  statusAtivo: FiltroStatus;
  onSelect: (value: FiltroStatus) => void;
  isLoading?: boolean;
}

/**
 * Faixa de KPIs que TAMBÉM filtra: clicar num card aplica aquele status
 * (Total limpa o filtro). O card ativo ganha borda na cor do status.
 */
export function ExtratoKpis({ resumo, statusAtivo, onSelect, isLoading }: Props) {
  const pctConcluido =
    resumo && resumo.total > 0 ? Math.round((resumo.concluido / resumo.total) * 100) : 0;

  return (
    <SimpleGrid columns={{ base: 2, md: 3, lg: 6 }} spacing={4} mb={6}>
      {KPIS.map((kpi, idx) => {
        const ativo = statusAtivo === kpi.value;
        const count = kpi.somaKeys
          ? kpi.somaKeys.reduce((acc, k) => acc + (resumo?.[k] ?? 0), 0)
          : (resumo?.[kpi.countKey] ?? 0);
        const atencao = !!kpi.requerAtencao && count > 0;
        return (
          <motion.div
            key={kpi.value || 'todos'}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.06 * idx, ease: [0.4, 0, 0.2, 1] }}
          >
            <GlassCard
              intensity="strong"
              hoverable
              p={5}
              h="full"
              cursor="pointer"
              role="button"
              tabIndex={0}
              aria-pressed={ativo}
              aria-label={`Filtrar por ${kpi.label}`}
              borderWidth={ativo ? '2px' : '1px'}
              borderColor={ativo ? kpi.accent : 'rgba(255, 255, 255, 0.6)'}
              onClick={() => onSelect(kpi.value)}
              onKeyDown={ev => {
                if (ev.key === 'Enter' || ev.key === ' ') {
                  ev.preventDefault();
                  onSelect(kpi.value);
                }
              }}
            >
              <Flex direction="column" gap={2} h="full">
                <Flex align="center" justify="space-between">
                  <Text
                    fontSize="10px"
                    fontWeight="600"
                    color="#94A3B8"
                    textTransform="uppercase"
                    letterSpacing="0.08em"
                  >
                    {kpi.label}
                  </Text>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    w="32px"
                    h="32px"
                    borderRadius="lg"
                    bg={`${kpi.accent}14`}
                    color={kpi.accent}
                  >
                    {kpi.icon}
                  </Box>
                </Flex>
                {isLoading && !resumo ? (
                  <Skeleton height="32px" width="60%" mt={1} borderRadius="md" />
                ) : (
                  <Text
                    fontSize={{ base: '24px', md: '28px' }}
                    fontWeight="700"
                    color="#0F172A"
                    letterSpacing="-0.02em"
                    lineHeight="1.1"
                  >
                    {fmtNumero(count)}
                  </Text>
                )}
                {kpi.value === 'concluido' && resumo && resumo.total > 0 && (
                  <Text fontSize="11px" color={kpi.accent} fontWeight="600">
                    {pctConcluido}% do total
                  </Text>
                )}
                {atencao && (
                  <Text fontSize="11px" color={kpi.accent} fontWeight="600">
                    requer atenção
                  </Text>
                )}
              </Flex>
            </GlassCard>
          </motion.div>
        );
      })}
    </SimpleGrid>
  );
}
